import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Star, ExternalLink, Check, AlertCircle, Chrome, Facebook, MapPin, Home, BedDouble } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  chrome: Chrome,
  facebook: Facebook,
  'map-pin': MapPin,
  star: Star,
  home: Home,
  'bed-double': BedDouble,
};

const GDPRNotice = () => (
  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="gdpr-notice">
    <p className="text-xs text-gray-500 leading-relaxed">
      By submitting a review, you agree that any personal information you provide will be shared with the business you are reviewing. You have the right to request erasure of your data by contacting that business directly. Continued use of this site indicates your acceptance of this.{' '}
      <a
        href="https://www.reviewmybusiness.ie/privacy/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
        data-testid="gdpr-privacy-link"
      >
        Full GDPR Privacy Notice
      </a>
    </p>
  </div>
);

const PublicRatingPage = () => {
  const { code } = useParams();
  const [plaque, setPlaque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('rating'); // rating, feedback, redirect, positive_feedback, submitted
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  const [feedback, setFeedback] = useState({
    text: '',
    name: '',
    email: '',
    mobile: ''
  });

  useEffect(() => {
    fetchPlaque();
  }, [code]);

  const fetchPlaque = async () => {
    try {
      const response = await axios.get(`${API}/r/${code}`);
      setPlaque(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This rating plaque was not found.');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'This rating plaque has not been activated yet.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSelect = (rating) => {
    setSelectedRating(rating);
    
    if (rating >= plaque.threshold_stars) {
      setStep('redirect');
    } else {
      setStep('feedback');
    }
  };

  const submitRating = async (includesFeedback = false) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/r/${code}/rate`, {
        stars: selectedRating,
        feedback: includesFeedback ? feedback.text : null,
        customer_name: includesFeedback ? feedback.name : null,
        customer_email: includesFeedback ? feedback.email : null,
        customer_mobile: includesFeedback ? feedback.mobile : null
      });
      
      if (step === 'redirect') {
        // Track the click-through before redirecting
        try {
          await axios.post(`${API}/r/${code}/track-click/${response.data.id}`);
        } catch (e) {
          // Don't block redirect if tracking fails
        }
        window.location.href = plaque.review_url;
      } else {
        setStep('submitted');
        toast.success('Thank you for your feedback!');
      }
    } catch (err) {
      toast.error('Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoThanks = () => {
    setFeedback({ text: '', name: '', email: '', mobile: '' });
    setStep('positive_feedback');
  };

  const submitPositiveFeedback = async () => {
    setSubmitting(true);
    try {
      const hasFeedback = feedback.text || feedback.name || feedback.email || feedback.mobile;
      await axios.post(`${API}/r/${code}/rate`, {
        stars: selectedRating,
        feedback: hasFeedback ? feedback.text : null,
        customer_name: hasFeedback ? feedback.name : null,
        customer_email: hasFeedback ? feedback.email : null,
        customer_mobile: hasFeedback ? feedback.mobile : null
      });
      setStep('submitted');
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const PlatformIcon = plaque ? (platformIcons[plaque.platform_icon] || Star) : Star;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <Card className="max-w-md w-full shadow-layered border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="font-dashboard-heading text-xl font-bold mb-2">Oops!</h1>
            <p className="text-[var(--color-text-secondary)]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50 p-6">
      <Card className="max-w-md w-full shadow-layered border-0">
        <CardContent className="pt-8 pb-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <PlatformIcon className="w-8 h-8 text-[var(--color-accent)]" />
            </div>
            <h1 className="font-dashboard-heading text-2xl font-bold mb-1" data-testid="business-name">
              {plaque.business_name}
            </h1>
            <p className="text-[var(--color-text-muted)]">
              {step === 'rating' && 'How was your experience?'}
              {step === 'feedback' && "We'd love to hear more"}
              {step === 'redirect' && 'Thanks for your rating!'}
              {step === 'positive_feedback' && 'We appreciate your time!'}
              {step === 'submitted' && 'Thank you!'}
            </p>
          </div>

          {/* Rating Step */}
          {step === 'rating' && (
            <div className="animate-fade-in-up">
              <div className="flex justify-center gap-2 mb-6" data-testid="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRatingSelect(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-2 transition-transform hover:scale-110 active:scale-95"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-12 h-12 transition-colors ${
                        star <= (hoveredRating || selectedRating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-200'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-[var(--color-text-muted)]">
                Tap a star to rate
              </p>
            </div>
          )}

          {/* Feedback Step (below threshold) */}
          {step === 'feedback' && (
            <div className="animate-fade-in-up space-y-4">
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-6 h-6 ${
                      star <= selectedRating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-200'
                    }`}
                  />
                ))}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="feedback">What could we improve?</Label>
                <Textarea
                  id="feedback"
                  placeholder="Tell us about your experience..."
                  value={feedback.text}
                  onChange={(e) => setFeedback({ ...feedback, text: e.target.value })}
                  rows={4}
                  data-testid="feedback-text"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Name (optional)</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={feedback.name}
                  onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                  data-testid="feedback-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Your email address"
                  value={feedback.email}
                  onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
                  data-testid="feedback-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number (optional)</Label>
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="Your phone number"
                  value={feedback.mobile}
                  onChange={(e) => setFeedback({ ...feedback, mobile: e.target.value })}
                  data-testid="feedback-mobile"
                />
                <p className="text-xs text-[var(--color-text-muted)]">
                  So we can reach out to make things right
                </p>
              </div>

              <GDPRNotice />
              
              <Button 
                onClick={() => submitRating(true)}
                disabled={submitting}
                className="w-full h-12 rounded-full btn-lift"
                data-testid="submit-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          )}

          {/* Redirect Step (at or above threshold) */}
          {step === 'redirect' && (
            <div className="animate-fade-in-up text-center">
              <div className="flex justify-center gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-8 h-8 ${
                      star <= selectedRating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-200'
                    }`}
                  />
                ))}
              </div>
              
              <p className="text-[var(--color-text-secondary)] mb-6">
                We're glad you had a great experience! Would you mind sharing your review on {plaque.platform_name}?
              </p>
              
              <Button 
                onClick={() => submitRating(false)}
                disabled={submitting}
                className="w-full h-12 rounded-full btn-lift"
                data-testid="leave-review-btn"
              >
                {submitting ? 'Redirecting...' : (
                  <>
                    Leave a Review
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              <button 
                onClick={handleNoThanks}
                className="text-sm text-[var(--color-text-muted)] mt-4 hover:underline block mx-auto"
                data-testid="skip-review-btn"
              >
                No thanks, I'm done
              </button>
            </div>
          )}

          {/* Positive Feedback Step (clicked "No thanks") */}
          {step === 'positive_feedback' && (
            <div className="animate-fade-in-up space-y-4">
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`w-6 h-6 ${
                      star <= selectedRating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-200'
                    }`}
                  />
                ))}
              </div>

              <p className="text-center text-sm text-[var(--color-text-secondary)] mb-2">
                Before you go, would you like to share any feedback about your experience? All fields are optional.
              </p>

              <div className="space-y-2">
                <Label htmlFor="posFeedback">Feedback (optional)</Label>
                <Textarea
                  id="posFeedback"
                  placeholder="Tell us about your experience..."
                  value={feedback.text}
                  onChange={(e) => setFeedback({ ...feedback, text: e.target.value })}
                  rows={3}
                  data-testid="positive-feedback-text"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="posName">Name (optional)</Label>
                <Input
                  id="posName"
                  placeholder="Your name"
                  value={feedback.name}
                  onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                  data-testid="positive-feedback-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="posEmail">Email Address (optional)</Label>
                <Input
                  id="posEmail"
                  type="email"
                  placeholder="Your email address"
                  value={feedback.email}
                  onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
                  data-testid="positive-feedback-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="posMobile">Mobile Number (optional)</Label>
                <Input
                  id="posMobile"
                  type="tel"
                  placeholder="Your phone number"
                  value={feedback.mobile}
                  onChange={(e) => setFeedback({ ...feedback, mobile: e.target.value })}
                  data-testid="positive-feedback-mobile"
                />
              </div>

              <GDPRNotice />

              <Button 
                onClick={submitPositiveFeedback}
                disabled={submitting}
                className="w-full h-12 rounded-full btn-lift"
                data-testid="submit-positive-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
              
              <button 
                onClick={() => {
                  submitRating(false);
                }}
                className="text-sm text-[var(--color-text-muted)] hover:underline block mx-auto"
                data-testid="skip-feedback-btn"
              >
                Skip, I'm done
              </button>
            </div>
          )}

          {/* Submitted Step */}
          {step === 'submitted' && (
            <div className="animate-fade-in-up text-center">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-dashboard-heading text-xl font-bold mb-2">
                Thank You!
              </h2>
              <p className="text-[var(--color-text-secondary)]">
                Your feedback has been submitted. We appreciate you taking the time to help us improve.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicRatingPage;
